import * as React from 'react'
import ModalLayout from '@/modules/drivers/modals/shared/ModalLayout'
import { FaIcon, faClose } from '@/lib/fa'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { FieldsetSelect, Option } from '@/modules/settings/components/FieldsetSelect'
import {
  useQueryServers,
  useMutationStartTransfer,
  useTransferStatus,
  isTransferInProgress,
} from '@/lib/api/database/useDatabaseTransferHooks'

interface TransferDatabaseModalProps {
  isOpen: boolean
  close: () => void
  databaseId: number
  databaseName: string
  currentServerId: number
  /** Called once the transfer completes successfully, so the caller can refresh its list. */
  onTransferred?: () => void
}

const TransferDatabaseModal: React.FC<TransferDatabaseModalProps> = ({
  isOpen,
  close,
  databaseId,
  databaseName,
  currentServerId,
  onTransferred,
}) => {
  const { t } = useLocalize()
  const { data: servers = [] } = useQueryServers()
  const startTransfer = useMutationStartTransfer()

  const [selectedServerId, setSelectedServerId] = React.useState<string>('')
  const [jobId, setJobId] = React.useState<string | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const notifiedRef = React.useRef(false)

  const { data: status } = useTransferStatus(jobId)
  const inProgress = jobId != null && isTransferInProgress(status?.status)
  const completed = status?.status === 'completed'
  const failed = status?.status === 'failed'

  React.useEffect(() => {
    if (isOpen) {
      setSelectedServerId('')
      setJobId(null)
      setError(null)
      notifiedRef.current = false
    }
  }, [isOpen])

  // Notify the parent once, when the job finishes successfully.
  React.useEffect(() => {
    if (completed && !notifiedRef.current) {
      notifiedRef.current = true
      onTransferred?.()
    }
  }, [completed, onTransferred])

  const serverOptions: Option[] = React.useMemo(() => {
    return servers
      .filter((s) => s.id !== currentServerId)
      .map((s) => ({
        label: `${s.name}${s.address ? ` (${s.address})` : ''}${
          s.isPrivate ? ' (private)' : ''
        }`,
        value: String(s.id),
      }))
  }, [servers, currentServerId])

  const handleStart = async () => {
    setError(null)
    if (!selectedServerId) {
      setError(t('administrationPanel-databases.transfer-select-server'))
      return
    }
    try {
      const result = await startTransfer.mutateAsync({
        sourceDatabaseId: databaseId,
        targetServerId: Number(selectedServerId),
      })
      setJobId(result.jobId)
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          t('administrationPanel-databases.transfer-failed'),
      )
    }
  }

  if (!isOpen) return null

  const progress = status?.progress ?? 0
  const stepText = status?.step ?? ''

  return (
    <ModalLayout>
      <div className="tw-relative tw-flex tw-max-h-[90vh] tw-w-full tw-max-w-[520px] tw-flex-col tw-rounded-lg tw-bg-white">
        {/* Header */}
        <div className="tw-border-b tw-border-base-300 tw-px-4 tw-py-4">
          <div className="tw-flex tw-items-center tw-justify-between">
            <p className="tw-text-xl tw-font-semibold tw-text-content-base">
              {t('administrationPanel-databases.transfer-title')}
            </p>
            <FaIcon
              icon={faClose}
              className="tw-h-5 tw-w-5 tw-cursor-pointer"
              onClick={close}
            />
          </div>
        </div>

        {/* Content */}
        <div className="tw-flex-1 tw-overflow-y-auto tw-px-4 tw-py-6">
          <div className="tw-space-y-4">
            <div className="tw-rounded-lg tw-border tw-border-blue-200 tw-bg-blue-50 tw-p-4">
              <p className="tw-text-sm tw-text-blue-800">
                {t('administrationPanel-databases.name')}:{' '}
                <strong>{databaseName}</strong>
              </p>
            </div>

            {/* Warning: access is blocked during the transfer */}
            {!jobId && (
              <div className="tw-rounded-lg tw-border tw-border-amber-200 tw-bg-amber-50 tw-p-4">
                <p className="tw-text-sm tw-text-amber-800">
                  {t('administrationPanel-databases.transfer-access-warning')}
                </p>
              </div>
            )}

            {!jobId && (
              <FieldsetSelect
                label={t('administrationPanel-databases.transfer-select-server')}
                placeholder={t('administrationPanel-databases.transfer-choose-server')}
                options={serverOptions}
                value={selectedServerId}
                searchEnabled
                handleChange={(value) => setSelectedServerId(value)}
                name="targetServerId"
                required
              />
            )}

            {/* Progress */}
            {jobId && (
              <div className="tw-space-y-2">
                <div className="tw-flex tw-items-center tw-justify-between tw-text-sm tw-text-content-base">
                  <span>{stepText}</span>
                  <span className="tw-font-semibold">{progress}%</span>
                </div>
                <div className="tw-h-2 tw-w-full tw-overflow-hidden tw-rounded-full tw-bg-base-200">
                  <div
                    className={`tw-h-full tw-rounded-full tw-transition-all ${
                      failed ? 'tw-bg-red-500' : 'tw-bg-primary'
                    }`}
                    style={{ width: `${Math.max(progress, failed ? 100 : 3)}%` }}
                  />
                </div>
                {inProgress && (
                  <p className="tw-text-xs tw-text-amber-700">
                    {t('administrationPanel-databases.transfer-in-progress-blocked')}
                  </p>
                )}
                {completed && (
                  <p className="tw-text-sm tw-font-medium tw-text-green-700">
                    {t('administrationPanel-databases.transfer-completed')}
                  </p>
                )}
              </div>
            )}

            {(error || failed) && (
              <div className="tw-rounded-lg tw-border tw-border-red-200 tw-bg-red-50 tw-p-4">
                <p className="tw-text-sm tw-text-red-800">
                  {error ||
                    status?.errorMessage ||
                    t('administrationPanel-databases.transfer-failed')}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="tw-border-t tw-border-base-300 tw-px-4 tw-py-4">
          <div className="tw-flex tw-justify-end tw-gap-3">
            <button
              type="button"
              onClick={close}
              className="tw-rounded-lg tw-border tw-border-base-300 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-content-base hover:tw-bg-base-100"
            >
              {completed || failed
                ? t('administrationPanel-databases.transfer-close')
                : t('administrationPanel-databases.transfer-cancel')}
            </button>
            {!jobId && (
              <button
                type="button"
                onClick={handleStart}
                disabled={!selectedServerId || startTransfer.isPending}
                className="tw-rounded-lg tw-bg-primary tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white hover:tw-opacity-90 disabled:tw-opacity-50"
              >
                {t('administrationPanel-databases.transfer-start')}
              </button>
            )}
          </div>
        </div>
      </div>
    </ModalLayout>
  )
}

export default TransferDatabaseModal
